import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useMe } from '../session.tsx';
import { triggerWords } from './feedback-context.ts';

/**
 * A screen that throws while drawing shows a sentence and a way on, never a
 * blank page. Keyed by path where it is used, so moving to another page
 * starts clean.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    return error ? <Broken error={error} /> : this.props.children;
  }
}

/** The page that broke: what to do next, in the words of the reader's own button (B43: a client's reads Ask or report). */
function Broken({ error }: { error: Error }) {
  const { data: me } = useMe();
  return (
    <main className="shell pb-28 pt-8 sm:pt-12">
      <h1 className="text-[2.125rem] sm:text-[2.5rem]">This page broke</h1>
      <p className="mt-3 text-ink-2">
        Something failed while drawing it.{me ? ` Report it with ${triggerWords(me)}, at the bottom right; the message below helps.` : ' The message below says what.'}
      </p>
      <p className="meta mt-2 break-words">{error.message}</p>
      <p className="mt-6 flex gap-6">
        <a className="link" href={window.location.pathname}>
          Reload
        </a>
        <a className="link" href="/">
          Home
        </a>
      </p>
    </main>
  );
}
